import type {
  AlternateObject,
  StoryblokComponent,
  StoryblokResult,
  StoryData,
} from 'storyblok-js-client';
import { globalSettingsSlug, StoryApiVersionType, StorySlug } from './types/api.types';
import type { StoryApiVersion, StaticPaths } from './types/api.types';
import config from '../../locale.config';
import type { CaseCarouselBlokData } from '../../components/blocks/case-carousel/CaseCarousel.data';
import type { CaseListingBlokData } from '../../components/blocks/case-listing/CaseListing.data';
import type { FeaturedNewsOverviewBlokData } from '../../components/blocks/featured-news-overview/FeaturedNewsOverview.data';
import type { RelatedNewsBlok } from '../../components/blocks/related-news/RelatedNews';
import type { Case, StoryContent } from './types/global.types';
import type { LocationsBlokData } from '../../components/blocks/locations/Locations.data';
import type { LocationBlokData } from '../../components/blocks/location/Location.data';
import ParamName from '@/router/ParamName';

export const getStoryApiVersion = (preview?: boolean): StoryApiVersion =>
  preview ? StoryApiVersionType.Draft : StoryApiVersionType.Published;

let StoryblokSpaceVersion: number = 0;

export const updateCacheVersion = (version: number): void => {
  StoryblokSpaceVersion = version;
};

export const getDefaultParams = (preview?: boolean): { cv?: number; version: StoryApiVersion } => {
  const params: { cv?: number; version: StoryApiVersion } = {
    version: getStoryApiVersion(preview),
  };

  if (preview) {
    StoryblokSpaceVersion = Date.now();
  }

  params.cv = StoryblokSpaceVersion;

  return params;
};

export const formatPath = (
  slug: string | Array<string> | boolean,
  locales?: Array<string>,
  paramName: string = ParamName.Slug,
): StaticPaths => {
  const excludePaths = [globalSettingsSlug];
  const paths: StaticPaths = [];

  if (locales && locales.length > 0) {
    let newSlug = slug;
    if (newSlug && (newSlug as Array<string>).length > 0) {
      // (newSlug as Array<string>).splice(0, 1);

      if ((newSlug as Array<string>)[0] === StorySlug.Home) newSlug = false;
    }

    if (!excludePaths.includes((newSlug as Array<string>)[0])) {
      locales.forEach((locale) => paths.push({ params: { [paramName]: newSlug }, locale }));
    }
  } else {
    paths.push({ params: { [paramName]: slug } });
  }

  return paths;
};

export const formatPaths = (
  links: { [key: string]: AlternateObject },
  // locales?: Array<string>,
  paramName: string = ParamName.Slug,
  catchAllRoute: boolean = true,
  excludePaths: Array<string> = [StorySlug.Home],
): StaticPaths => {
  let paths: StaticPaths = [];

  Object.keys(links).forEach((linkKey) => {
    if (links[linkKey].is_folder || excludePaths.includes(links[linkKey].slug)) {
      return;
    }

    const { slug } = links[linkKey];
    // Get Array for slug because of catch-all-routes (https://nextjs.org/docs/routing/dynamic-routes#catch-all-routes)
    const pathSlug: Array<string> | string | boolean = catchAllRoute ? slug.split('/') : slug;
    // if (slug === StorySlug.Home) pathSlug = false;
    const { locales } = config;
    paths = paths.concat(formatPath(pathSlug, locales as unknown as Array<string>, paramName));
  });

  return paths;
};

export const stripCase = (c: StoryData<Case>): void => {
  delete c.content?.modules;

  c.content?.locations?.forEach((location) => {
    if (location.alternates) {
      // @ts-ignore
      delete location.alternates;
    }
    delete location.content?.sections;
    delete location.content?.office_details;
  });
};

export const stripResponse = (response: StoryblokResult): StoryblokResult => {
  // Delete unnecessary modules we don't need when using resolve_relations for cases: https://nextjs.org/docs/messages/large-page-data
  // (should probably refactor REST to GraphQL to exclude data from resolve_relations)
  if (response?.data?.story?.alternates) {
    delete response?.data?.story?.alternates;
  }
  if (response?.data?.story?.content?.modules) {
    response.data.story.content.modules.forEach((module: StoryblokComponent<string>) => {
      if (module.component === 'case_carousel' || module.component === 'case_listing') {
        (module as unknown as CaseCarouselBlokData | CaseListingBlokData).cards.forEach((card) => {
          stripCase(card.case);
        });
      }
      if (module.component === 'locations_blok') {
        // @ts-ignore

        delete (module as unknown as LocationsBlokData).locations;
      }
      if (module.component === 'case_listing') {
        (module as unknown as CaseListingBlokData)?.locations_filter.forEach((locationsFilter) => {
          if (locationsFilter) {
            delete locationsFilter?.content?.sections;
            delete locationsFilter?.content?.office_details;
          }
        });
      }
      if (module.component === 'featured_news_overview') {
        (module as unknown as FeaturedNewsOverviewBlokData).news_items.forEach((item) => {
          delete (item.news.content as { modules?: Array<StoryblokComponent<string>> })?.modules;
        });
      }
      if (module.component === 'related_news') {
        (module as RelatedNewsBlok).news_items?.forEach((item) => {
          if (typeof item !== 'string' && item?.content) {
            delete (item.content as { modules?: Array<StoryblokComponent<string>> }).modules;
          }
        });
      }
    });
  }

  return response;
};

export const stripAllCasesResponse = (
  response: StoryblokResult,
): Array<StoryData<Case>> | undefined => {
  // Delete unnecessary modules we don't need when using resolve_relations for cases: https://nextjs.org/docs/messages/large-page-data
  // (should probably refactor REST to GraphQL to exclude data from resolve_relations)
  if (response?.data?.stories) {
    response.data.stories.forEach((story: StoryData<Case>) => stripCase(story));
    return response.data.stories;
  }

  return undefined;
};

const keysToKeep = ['content', 'full_slug', 'uuid'];
export const stripNextCaseResponse = (
  nextCaseStory: StoryData,
): StoryData<StoryContent & StoryblokComponent<string>> | undefined => {
  // Delete unnecessary modules we don't need when using resolve_relations for cases: https://nextjs.org/docs/messages/large-page-data
  // (should probably refactor REST to GraphQL to exclude data from resolve_relations)
  if (nextCaseStory) {
    const nextCase = nextCaseStory;
    Object.keys(nextCase).forEach((key) => {
      if (!keysToKeep.includes(key)) {
        // @ts-ignore
        delete nextCase[key];
      }
    });
    return nextCase;
  }

  return undefined;
};

export const stripLocationsBlockResponse = (
  locationsBlok: LocationsBlokData,
): Array<LocationBlokData> | undefined => {
  // Delete unnecessary modules we don't need when using resolve_relations for cases: https://nextjs.org/docs/messages/large-page-data
  // (should probably refactor REST to GraphQL to exclude data from resolve_relations)

  if (locationsBlok?.locations) {
    return locationsBlok?.locations.map((location: LocationBlokData) => ({
      component: location.component,
      countries: [],
      title: location.title,
      office_link: {
        fieldtype: location.office_link.fieldtype,
        id: location.office_link.id,
        linktype: location.office_link.linktype,
        url: location.office_link.url,
        target: location.office_link.target,
        cached_url: location.office_link.cached_url,
      },
      city: location.city,
      region: location.region,
      country: location.country,
      _uid: location._uid,
    }));
  }
  return undefined;
};
